import { Product } from "@deliverr/commons-clients";
import { ProductCasePack } from "@deliverr/commons-clients/lib/product/ProductCasePack";
import { CasePackDefaultData } from "common/clients/inbound/bulkUpload/CasePackDefaultData";
import { useCallback } from "react";
import { useDispatch, batch } from "react-redux";
import {
  setSelectedCasePackOption,
  setMultipleProductCasePackOptions,
} from "../../../../selectProductCasePacks/SelectProductCasePackActions";
import { useCasePackTemplate, CasePackCreationRequest } from "./useCasePackTemplate";
import { toast } from "common/components/ui";
import { log } from "@deliverr/commons-logs";
import { getUnifiedProducts } from "common/wholesale-common/getUnifiedProducts";
import { BATCH_PROCESSING, chunkArray } from "common/constants/processing.const";

export interface ProductCasePackProcessingRequest {
  product: Product & { qty: number };
  caseDimensions: CasePackDefaultData[];
  existingCasePacks: ProductCasePack[];
}

export const useCasePackOperations = () => {
  const dispatch = useDispatch();
  const { createCasePackTemplate, createBulkCasePackTemplates } = useCasePackTemplate();

  const fetchProductCasePacksForMultipleProducts = useCallback(
    async (dskus: string[]): Promise<Record<string, ProductCasePack[]>> => {
      if (!dskus.length) {
        return {};
      }

      try {
        // Chunk DSKUs for better performance with large datasets
        const dskuChunks = chunkArray(dskus, BATCH_PROCESSING.PRODUCT_FETCH_BATCH_SIZE);
        const result: Record<string, ProductCasePack[]> = {};
        const allCasePackOptions: Array<{ dsku: string; productCasePacks: Record<string, ProductCasePack> }> = [];

        // Process chunks with limited concurrency to avoid overwhelming the API
        const chunkPromises = dskuChunks.map(async (dskuChunk, index) => {
          // Add small delay between chunks to prevent API rate limiting
          if (index > 0) {
            await new Promise((resolve) => setTimeout(resolve, 100));
          }

          try {
            const productDetails = await dispatch(getUnifiedProducts(dskuChunk));

            dskuChunk.forEach((dsku) => {
              const productCasePacks = productDetails[dsku]?.productCasePacks ?? [];
              result[dsku] = productCasePacks;

              if (productCasePacks.length > 0) {
                const productCasePackOptions: Record<string, ProductCasePack> = {};
                productCasePacks.forEach((casePack) => {
                  if (casePack?.dsku) {
                    productCasePackOptions[casePack.dsku] = casePack;
                  }
                });
                allCasePackOptions.push({ dsku, productCasePacks: productCasePackOptions });
              }
            });
          } catch (error) {
            log.error(`Failed to fetch case packs for chunk ${index}:`, error);
            // Initialize empty arrays for failed chunks so processing can continue
            dskuChunk.forEach((dsku) => {
              result[dsku] = [];
            });
          }
        });

        // Process chunks with limited concurrency
        const concurrentLimit = BATCH_PROCESSING.MAX_CONCURRENT_OPERATIONS;
        for (let i = 0; i < chunkPromises.length; i += concurrentLimit) {
          const batches = chunkPromises.slice(i, i + concurrentLimit);
          await Promise.all(batches);
        }

        // Batch dispatch case pack options for better Redux performance
        if (allCasePackOptions.length > 0) {
          const optionsChunks = chunkArray(allCasePackOptions, BATCH_PROCESSING.REDUX_ACTION_BATCH_SIZE);
          optionsChunks.forEach((chunk) => {
            batch(() => {
              dispatch(setMultipleProductCasePackOptions(chunk));
            });
          });
        }

        return result;
      } catch (err) {
        log.error("Failed to fetch case pack data:", err);
        toast.error("Failed to fetch case pack data. Please try again.");
        return {};
      }
    },
    [dispatch]
  );

  /**
   * Highly optimized bulk version with chunked processing for large datasets
   * Target: <30 seconds for 1000 products with existing case packs
   */
  const processCasePacksForMultipleProducts = useCallback(
    async (requests: ProductCasePackProcessingRequest[]): Promise<Record<string, ProductCasePack[]>> => {
      if (requests.length === 0) {
        return {};
      }

      try {
        const result: Record<string, ProductCasePack[]> = {};
        const bulkCasePackCreationRequests: CasePackCreationRequest[] = [];
        const bulkCasePackOptions: Record<string, Record<string, ProductCasePack>> = {};

        // Phase 1: Process existing case packs in chunks to avoid UI blocking
        const requestChunks = chunkArray(requests, BATCH_PROCESSING.PROCESSING_CHUNK_SIZE);

        for (const requestChunk of requestChunks) {
          // Use setTimeout to yield control back to the event loop
          await new Promise((resolve) => setTimeout(resolve, 0));

          requestChunk.forEach(({ product, caseDimensions, existingCasePacks }) => {
            const dsku = product.dsku;
            if (!dsku) {
              log.warn("Skipping request with missing product DSKU");
              return;
            }

            const matchingCasePacks: ProductCasePack[] = [];
            result[dsku] = [];

            // Validate case dimensions efficiently
            const validCaseDimensions = caseDimensions.filter((cd) => {
              return cd.unitsPerCase && cd.unitsPerCase > 0;
            });

            if (validCaseDimensions.length !== caseDimensions.length) {
              const skippedCount = caseDimensions.length - validCaseDimensions.length;
              log.warn(`Skipped ${skippedCount} invalid case dimension(s) for product ${dsku}`);
            }

            // Pre-index existing case packs by quantity for O(1) lookup
            const existingCasePacksByQuantity = new Map<number, ProductCasePack>();
            existingCasePacks.forEach((casePack) => {
              if (casePack?.quantity && casePack.quantity > 0) {
                existingCasePacksByQuantity.set(casePack.quantity, casePack);
              }
            });

            validCaseDimensions.forEach((caseDimension) => {
              const matchingCasePack = existingCasePacksByQuantity.get(caseDimension.unitsPerCase);

              if (matchingCasePack) {
                matchingCasePacks.push(matchingCasePack);
                if (matchingCasePack?.dsku) {
                  if (!bulkCasePackOptions[dsku]) {
                    bulkCasePackOptions[dsku] = {};
                  }
                  bulkCasePackOptions[dsku][matchingCasePack.dsku] = matchingCasePack;
                }
              } else {
                bulkCasePackCreationRequests.push({
                  product,
                  caseDimension,
                });
              }
            });

            result[dsku] = matchingCasePacks;
          });
        }

        // Phase 2: Bulk create missing case packs in batches
        if (bulkCasePackCreationRequests.length > 0) {
          const creationChunks = chunkArray(
            bulkCasePackCreationRequests,
            BATCH_PROCESSING.CASE_PACK_CREATION_BATCH_SIZE
          );

          for (const chunk of creationChunks) {
            try {
              const createdCasePacksByProduct = await createBulkCasePackTemplates(chunk);

              // Merge created case packs efficiently
              Object.entries(createdCasePacksByProduct).forEach(([dsku, newCasePacks]) => {
                if (!result[dsku]) {
                  result[dsku] = [];
                }
                result[dsku].push(...newCasePacks);
              });
            } catch (error) {
              log.error(`Bulk case pack creation failed for chunk, falling back to individual creation:`, error);

              // Fallback: Use individual creation for failed chunk
              const fallbackResults = await Promise.allSettled(
                chunk.map(async ({ product, caseDimension }) => {
                  const createdCasePack = await createCasePackTemplate(product, caseDimension);
                  if (createdCasePack) {
                    if (!result[product.dsku]) {
                      result[product.dsku] = [];
                    }
                    result[product.dsku].push(createdCasePack);
                  }
                  return createdCasePack;
                })
              );

              const failedCount = fallbackResults.filter((r) => r.status === "rejected").length;
              if (failedCount > 0) {
                log.error(`Failed to create ${failedCount} case packs in fallback`);
              }
            }
          }
        }

        // Phase 3: Batch dispatch case pack options efficiently
        if (Object.keys(bulkCasePackOptions).length > 0) {
          const bulkCasePackOptionsArray = Object.entries(bulkCasePackOptions).map(([dsku, productCasePacks]) => ({
            dsku,
            productCasePacks,
          }));

          const optionsChunks = chunkArray(bulkCasePackOptionsArray, BATCH_PROCESSING.REDUX_ACTION_BATCH_SIZE);
          optionsChunks.forEach((chunk) => {
            batch(() => {
              dispatch(setMultipleProductCasePackOptions(chunk));
            });
          });
        }

        // Phase 4: Batch dispatch selected case packs efficiently
        const selectedCasePackUpdates: Record<string, ProductCasePack> = {};
        Object.entries(result).forEach(([dsku, casePacks]) => {
          if (casePacks.length > 0) {
            selectedCasePackUpdates[dsku] = casePacks[0];
          }
        });

        if (Object.keys(selectedCasePackUpdates).length > 0) {
          const updateChunks = chunkArray(
            Object.entries(selectedCasePackUpdates),
            BATCH_PROCESSING.REDUX_ACTION_BATCH_SIZE
          );
          updateChunks.forEach((chunk) => {
            batch(() => {
              chunk.forEach(([dsku, casePack]) => {
                dispatch(setSelectedCasePackOption(dsku, casePack));
              });
            });
          });
        }

        return result;
      } catch (error) {
        log.error("Critical error in bulk case pack processing:", error);
        toast.error("A critical error occurred while processing case packs. Please try again.");
        throw error;
      }
    },
    [createBulkCasePackTemplates, createCasePackTemplate, dispatch]
  );

  return {
    fetchProductCasePacksForMultipleProducts,
    processCasePacksForMultipleProducts,
  };
};
